import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18nInstance from "../i18n/i18n";

import {
  MdDashboard, MdPeople, MdCategory, MdVolunteerActivism,
  MdFavorite, MdTrendingUp, MdTrendingDown, MdCalendarMonth,
  MdPayment, MdBarChart, MdLogout, MdLanguage,
  MdFlashOn, MdAutoAwesome, MdPersonOutline, MdClose,
  MdOutlineCategory
} from "react-icons/md";
import ADevotees from "./Adevotees";
import ACategory from "./Acategory";
import AOfferings from "./Aofferings";
import ABookings from "./Abookings";
import ADonations from "./Adonations";
import AExpenseCategory from "./Aexpensecategory";
import AExpenses from "./Aexpenses";
import AReports from "./Areports";
import DashboardPage from "./DashboardPage";
import ACalendar from "./ACalendar";

/* ═══════════════════════════════════════════════════
   GOLDEN ICON WRAPPER
═══════════════════════════════════════════════════ */
function GoldIcon({ Icon, size = 20, glow = false }) {
  return (
    <span className={`gold-icon-wrap ${glow ? "gold-icon-glow" : ""}`} style={{ fontSize: size }}>
      <Icon />
    </span>
  );
}

/* ═══════════════════════════════════════════════════
   SIDEBAR MEDALLION — OM ↔ LOGO ANIMATION
═══════════════════════════════════════════════════ */
function SidebarMedallion({ collapsed }) {
  const [face, setFace] = useState("om");
  useEffect(() => {
    const t = setInterval(() => setFace(f => f === "om" ? "logo" : "om"), 3000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="sidebar-medallion">
      <div className="sm-ring" />
      <div className="sm-ring sm-ring-2" />
      <div className={`sm-face ${face === "om" ? "sm-face-visible" : ""}`}>
        <span className="sm-om">ॐ</span>
      </div>
      <div className={`sm-face ${face === "logo" ? "sm-face-visible" : ""}`}>
        <img src="./logo.png" alt="Temple" className="sm-logo-img" />
      </div>
      <div className="sm-halo" />
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   STUB PAGE COMPONENTS
═══════════════════════════════════════════════════ */
function StubPage({ titleKey, Icon }) {
  const { t } = useTranslation();
  return (
    <div className="stub-page">
      <div className="stub-icon-wrap"><GoldIcon Icon={Icon} size={52} glow /></div>
      <h2 className="stub-title">{t(titleKey)}</h2>
      <p className="stub-desc">
        {i18nInstance.language === "ml" ? "ഈ പേജ് നിർമ്മാണത്തിലാണ്..." : "This page is under construction..."}
      </p>
      <div className="stub-ornament">✦ ॐ ✦</div>
    </div>
  );
}



/* ═══════════════════════════════════════════════════
   SIDEBAR NAV ITEMS CONFIG
═══════════════════════════════════════════════════ */
const NAV_ITEMS = [
  { key: "dashboard", Icon: MdDashboard },
  { key: "calendar", Icon: MdCalendarMonth },
  { key: "devotees", Icon: MdPeople },
  { key: "offeringCategory", Icon: MdCategory },
  { key: "offerings", Icon: MdVolunteerActivism },
  { key: "donations", Icon: MdFavorite },
  // { key: "income",           Icon: MdTrendingUp },
  { key: "expenseCategory", Icon: MdOutlineCategory },
  { key: "expense", Icon: MdTrendingDown },
  { key: "bookings", Icon: MdCalendarMonth },
  // { key: "payments",         Icon: MdPayment },
  { key: "reports", Icon: MdBarChart },
];

/* ═══════════════════════════════════════════════════
   MAIN ADMIN LAYOUT
═══════════════════════════════════════════════════ */
export default function Admin() {
  const [activeSection, setActiveSection] = useState(() => {
    const savedSection = localStorage.getItem("adminActiveSection");
    return savedSection || "dashboard";
  });


  useEffect(() => {
    localStorage.setItem("adminActiveSection", activeSection);
  }, [activeSection]);

  // ── SESSION & AUTHENTICATION STATE ──
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // Loading state to prevent flicker

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("authToken");

    if (storedUser && token) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);

        // ── ROLE VALIDATION ──
        // If the user is NOT an admin, force logout/redirect immediately
        if (parsedUser.role !== 'admin') {
          console.warn("Access Denied: User is not an admin");
          localStorage.removeItem("authToken");
          localStorage.removeItem("user");
          window.location.href = '/login';
          return;
        }

      } catch (e) {
        console.error("Session parse error", e);
        localStorage.clear();
        window.location.href = '/login';
      }
    } else {
      // No session found
      window.location.href = '/login';
    }
    setLoading(false);
  }, []);

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();

  // ── HISTORY API / BACK BUTTON EXIT TRAP ──
  useEffect(() => {
    // Only apply trap if user is logged in
    if (!user) return;

    const applyTrap = () => {
      // If we've already trapped this specific history entry, don't double-push
      if (window.history.state && window.history.state.atAdminHome) return; 
      
      // Lay the trap by replacing the current blank state with the trap state
      window.history.replaceState({ isExitTrap: true }, "");
      // Then immediately push the /admin state forward so they have a state to pop back from
      window.history.pushState({ atAdminHome: true }, "", window.location.href);
    };

    applyTrap();

    const handlePopState = (e) => {
      // If the user navigates back and hits the trap state
      if (e.state && e.state.isExitTrap === true) {
        // Intercept it: if in dashboard, show modal. If NOT in dashboard, go to dashboard.
        if (activeSection === "dashboard") {
          setShowLogoutConfirm(true);
          window.history.pushState({ atAdminHome: true }, "", window.location.href);
        } else {
          setActiveSection("dashboard");
          window.history.pushState({ atAdminHome: true }, "", window.location.href);
        }
      } else {
        setShowLogoutConfirm(false);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [user, activeSection]);

  // Navigate to a section
  const navigateTo = (section) => {
    setActiveSection(section);
    setMobileSidebarOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("adminActiveSection");
    navigate("/login");
  };

  const toggleLang = (lng) => {
    i18nInstance.changeLanguage(lng);
    setLangMenuOpen(false);
  };

  const renderContent = () => {
    switch (activeSection) {
      case "devotees": return <ADevotees />;
      case "offeringCategory": return <ACategory />;
      case "offerings": return <AOfferings />;
      case "donations": return <ADonations />;
      case "income": return <StubPage titleKey="income" Icon={MdTrendingUp} />;
      case "expense": return <AExpenses />;
      case "expenseCategory": return <AExpenseCategory />;
      case "bookings": return <ABookings />;
      case "calendar": return <ACalendar />;
      case "payments": return <StubPage titleKey="payments" Icon={MdPayment} />;
      case "reports": return <AReports />;
      default: return <DashboardPage onNavigate={navigateTo} />;
    }
  };

  // Prevent rendering while checking session/role
  if (loading) return null;

  return (
    <>
      <AdminStyles sidebarOpen={sidebarOpen} />

      {/* ── ROOT: fixed full-viewport shell ── */}
      <div className={`admin-root ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}>

        {/* ── MOBILE OVERLAY ── */}
        {mobileSidebarOpen && (
          <div className="mobile-overlay" onClick={() => setMobileSidebarOpen(false)} />
        )}

        {/* ── SIDEBAR (fixed, full height) ── */}
        <aside className={`admin-sidebar ${mobileSidebarOpen ? "mobile-sidebar-open" : ""}`}>
          <div className="sidebar-top-strip" />

          <div className="sidebar-head">
            <SidebarMedallion />
            {sidebarOpen && (
              <div className="sidebar-brand">
                <div className="sb-title">{t("templeTitle")}</div>
                <div className="sb-sub">{t("adminPanel")}</div>
              </div>
            )}
            <button className="mobile-sidebar-close" onClick={() => setMobileSidebarOpen(false)}>
              <MdClose size={18} />
            </button>
          </div>

          {sidebarOpen && (
            <div className="sidebar-temple-sub">
              <div className="sts-dots"><span /><span /><span /><span /><span /></div>
              <div className="sts-text">{t("templeSub")}</div>
              <div className="sts-dots"><span /><span /><span /><span /><span /></div>
            </div>
          )}

          <div className="sidebar-rule" />

          <nav className="sidebar-nav">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.key}
                className={`nav-item ${activeSection === item.key ? "active" : ""}`}
                onClick={() => {
                  navigateTo(item.key);
                  setMobileSidebarOpen(false);
                }}
              >
                <span className="nav-icon"><item.Icon size={18} /></span>
                {sidebarOpen && <span className="nav-label">{t(item.key)}</span>}
                {sidebarOpen && <span className="nav-arrow">›</span>}
                {!sidebarOpen && <span className="nav-tooltip">{t(item.key)}</span>}
              </button>
            ))}
          </nav>

          <div className="sidebar-footer">
            <div className="sidebar-rule" />
            {sidebarOpen ? (
              <div className="sidebar-footer-inner">
                <div className="sf-ornament">✦ ॐ ✦</div>
                <div className="sf-temple">{t("ftCredit")}</div>
              </div>
            ) : (
              <div className="sf-collapsed-om">ॐ</div>
            )}
            <div className="sidebar-bottom-strip" />
          </div>
        </aside>

        {/* ── MAIN COLUMN (fills remaining width, fixed height) ── */}
        <div className="admin-main">

          {/* ── TOP NAVBAR (fixed at top of main column) ── */}
          <header className="admin-topbar">
            <div className="topbar-left">
              <button className="toggle-btn desktop-toggle" onClick={() => setSidebarOpen(o => !o)}>
                <span className={`toggle-icon ${sidebarOpen ? "open" : ""}`}>
                  <span /><span /><span />
                </span>
              </button>
              <button className="toggle-btn mobile-toggle" onClick={() => setMobileSidebarOpen(true)}>
                <span className="toggle-icon"><span /><span /><span /></span>
              </button>
              <Breadcrumb activeSection={activeSection} t={t} />
            </div>

            <div className="topbar-right">
              <div className="lang-wrapper">
                <button className="topbar-btn lang-btn" onClick={() => setLangMenuOpen(o => !o)}>
                  <MdLanguage size={15} className="tb-react-icon" />
                  <span className="tb-label">{i18nInstance.language === "en" ? "English" : "മലയാളം"}</span>
                  <span className="tb-caret">▾</span>
                </button>
                {langMenuOpen && (
                  <div className="lang-dropdown">
                    <button className={`lang-opt ${i18nInstance.language === "en" ? "active" : ""}`} onClick={() => toggleLang("en")}>
                      <span>EN</span> English
                    </button>
                    <button className={`lang-opt ${i18nInstance.language === "ml" ? "active" : ""}`} onClick={() => toggleLang("ml")}>
                      <span>ML</span> മലയാളം
                    </button>
                  </div>
                )}
              </div>

              {/* ── DYNAMIC USER BADGE ── */}
              <div className="admin-badge">
                <div className="badge-avatar">
                  <span>{user?.name ? user.name.charAt(0).toUpperCase() : 'A'}</span>
                  <div className="badge-ring" />
                </div>
                <div className="badge-info">
                  <span className="badge-name">{user?.name || "Administrator"}</span>
                  <span className="badge-role">{user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : "Admin"}</span>
                </div>
              </div>

              <button className="topbar-btn logout-btn" onClick={handleLogout}>
                <MdLogout size={15} className="tb-react-icon" />
                <span className="tb-label">{t("logout")}</span>
              </button>
            </div>
            <div className="topbar-gold-line" />
          </header>

          {/* ── SCROLLABLE CONTENT AREA ── */}
          <div className="admin-content-wrap">
            <main className="admin-content">
              {renderContent()}
            </main>
          </div>

          {/* ── MOBILE BOTTOM NAV ── */}
          <nav className="mobile-bottom-nav">
            {NAV_ITEMS.slice(0, 5).map((item) => (
              <button
                key={item.key}
                className={`mbn-item ${activeSection === item.key ? "active" : ""}`}
                onClick={() => navigateTo(item.key)}
              >
                <item.Icon size={20} />
                <span className="mbn-label">{t(item.key)}</span>
              </button>
            ))}
            <button className="mbn-item" onClick={() => setMobileSidebarOpen(true)}>
              <MdBarChart size={20} />
              <span className="mbn-label">{i18nInstance.language === "ml" ? "കൂടുതൽ" : "More"}</span>
            </button>
          </nav>
        </div>
      </div>

      {/* ── LOGOUT CONFIRM MODAL ── */}
      {showLogoutConfirm && (
        <div className="logout-overlay" onClick={() => {
          setShowLogoutConfirm(false);
          // When clicking backdrop to close, we should ensure the history state remains "atAdminHome"
        }}>
          <div className="logout-modal" onClick={e => e.stopPropagation()}>
            <div className="lm-topstrip" />
            <span className="lm-corner tl" /><span className="lm-corner tr" />
            <span className="lm-corner bl" /><span className="lm-corner br" />
            <div className="lm-icon-wrap">
              <div className="lm-icon-ring" />
              <div className="lm-icon-ring lm-icon-ring2" />
              <span className="lm-icon"><MdLogout /></span>
            </div>
            <div className="lm-eyebrow">✦ {t("adminPanel")} ✦</div>
            <h3 className="lm-title">{t("logout")}</h3>
            <div className="lm-divider"><span className="lm-gem" /></div>
            <p className="lm-message">
              {i18nInstance.language === "ml"
                ? "നിങ്ങൾ ലോഗൗട്ട് ചെയ്യാൻ ആഗ്രഹിക്കുന്നുണ്ടോ?"
                : "Are you sure you want to logout?"}
            </p>
            <div className="lm-actions">
              <button className="lm-btn lm-cancel" onClick={() => setShowLogoutConfirm(false)}>
                {t("btnCancel") || "Cancel"}
              </button>
              <button className="lm-btn lm-confirm" onClick={handleLogout}>
                <MdLogout size={14} />
                {t("logout") || "Logout"}
              </button>
            </div>
            <div className="lm-bottomstrip" />
          </div>
        </div>
      )}
    </>
  );
}

/* ── Breadcrumb ── */
function Breadcrumb({ activeSection, t }) {
  const pathMap = {
    "dashboard": t("dashboard"),
    "devotees": t("devotees"),
    "offeringCategory": t("offeringCategory"),
    "offerings": t("offerings"),
    "donations": t("donations"),
    "income": t("income"),
    "expense": t("expense"),
    "expenseCategory": t("expenseCategory"),
    "bookings": t("bookings"),
    "calendar": t("calendar"),
    "payments": t("payments"),
    "reports": t("reports"),
  };
  const label = pathMap[activeSection] || activeSection;
  return (
    <div className="breadcrumb">
      <span className="bc-root">admin</span>
      <span className="bc-sep">/</span>
      <span className="bc-current">{label}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════ */
function AdminStyles({ sidebarOpen }) {
  const sw = sidebarOpen ? "260px" : "68px";
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --crimson-deep:  #1a0404;
        --crimson-mid:   #2c0808;
        --crimson-rich:  #3d0c0c;
        --gold-bright:   #e8c46a;
        --gold-mid:      #c9973a;
        --gold-dim:      #8a6020;
        --gold-pale:     #f5e4b0;
        --parchment:     #FDF3E3;
        --parchment-dark:#f5e8cc;
        --text-dark:     #1a0404;
        --text-mid:      #4a1a08;
        --sidebar-w:     ${sw};
        --topbar-h:      62px;
        --transition:    0.28s cubic-bezier(0.4,0,0.2,1);
      }

      *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

      /* ── Prevent the page body itself from ever scrolling ── */
      html, body, #root {
        height: 100%;
        overflow: hidden;
      }

      /* ══ ROOT LAYOUT — fills viewport exactly ══ */
      .admin-root {
        display: flex;
        height: 100vh;
        width: 100vw;
        overflow: hidden;       /* nothing escapes the shell */
        font-family: 'EB Garamond', serif;
        background: var(--parchment);
        color: var(--text-dark);
      }

      /* ══ SIDEBAR — fixed, full height ══ */
      .admin-sidebar {
        width: var(--sidebar-w);
        height: 100vh;
        background:
          radial-gradient(ellipse at 50% 8%, rgba(160,35,35,0.4) 0%, transparent 45%),
          radial-gradient(ellipse at 20% 60%, rgba(80,15,15,0.35) 0%, transparent 45%),
          radial-gradient(ellipse at 80% 90%, rgba(100,20,20,0.2) 0%, transparent 40%),
          linear-gradient(175deg, #2e0c0c 0%, #1c0505 45%, #100202 100%);
        border-right: 1px solid rgba(201,151,58,0.2);
        display: flex;
        flex-direction: column;
        flex-shrink: 0;          /* never shrink */
        position: relative;
        z-index: 100;
        transition: width var(--transition);
        overflow: hidden;
        box-shadow: 6px 0 32px rgba(26,4,4,0.45), 1px 0 0 rgba(201,151,58,0.08);
      }
      .admin-sidebar::before {
        content: '';
        position: absolute; inset: 0; pointer-events: none; z-index: 0;
        background-image: url("data:image/svg+xml,%3Csvg width='200' height='200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
      }
      .admin-sidebar > * { position: relative; z-index: 1; }

      .sidebar-top-strip {
        height: 3px; flex-shrink: 0;
        background: linear-gradient(90deg, transparent, var(--gold-dim) 15%, var(--gold-bright) 40%, #fff8e7 55%, var(--gold-bright) 70%, var(--gold-dim) 85%, transparent);
      }

      .sidebar-head {
        display: flex; align-items: center; gap: 13px;
        padding: 20px 14px 12px; flex-shrink: 0;
      }

      .sidebar-medallion {
        width: 44px; height: 44px; border-radius: 50%;
        background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 50%, #0e0101 100%);
        border: 1px solid rgba(232,196,106,0.35);
        display: flex; align-items: center; justify-content: center;
        position: relative; flex-shrink: 0;
        box-shadow: 0 0 20px rgba(232,196,106,0.18), inset 0 1px 0 rgba(255,248,231,0.08);
      }
      .sm-ring {
        position: absolute; inset: -4px; border-radius: 50%;
        border: 1px dashed rgba(232,196,106,0.28);
        animation: slowSpin 10s linear infinite;
      }
      .sm-ring-2 {
        inset: -8px; border: 1px solid rgba(232,196,106,0.1);
        animation-duration: 18s; animation-direction: reverse; border-style: dotted;
      }
      @keyframes slowSpin { to { transform: rotate(360deg); } }
      .sm-om {
        font-family: 'Cinzel Decorative', cursive; font-size: 16px;
        background: linear-gradient(180deg, #fff8e7 0%, var(--gold-bright) 50%, var(--gold-mid) 100%);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        position: relative; z-index: 1;
        filter: drop-shadow(0 0 6px rgba(232,196,106,0.5));
      }
      .sm-halo {
        position: absolute; inset: -12px; border-radius: 50%;
        background: radial-gradient(circle, rgba(232,196,106,0.08) 0%, transparent 65%);
        pointer-events: none;
      }
      .sidebar-brand { overflow: hidden; white-space: nowrap; }
      .sb-title {
        font-family: 'Cinzel', serif; font-size: 13px; font-weight: 700; letter-spacing: 0.5px;
        background: linear-gradient(135deg, #fffaf0 0%, var(--gold-bright) 60%, var(--gold-mid) 100%);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.3;
      }
      .sb-sub {
        font-family: 'Cinzel', serif; font-size: 9.5px; letter-spacing: 2px; text-transform: uppercase;
        color: rgba(232,196,106,0.55); margin-top: 3px;
      }

      .sidebar-temple-sub {
        display: flex; align-items: center; gap: 6px;
        padding: 0 14px 10px; justify-content: center; overflow: hidden;
      }
      .sts-dots { display: flex; gap: 3px; align-items: center; flex-shrink: 0; }
      .sts-dots span {
        width: 3px; height: 3px; background: rgba(232,196,106,0.25);
        transform: rotate(45deg); display: block; flex-shrink: 0;
      }
      .sts-text {
        font-family: 'EB Garamond', serif; font-style: italic; font-size: 11.5px;
        color: rgba(245,228,176,0.5); white-space: nowrap; overflow: hidden; text-align: center;
      }

      .sidebar-rule {
        height: 1px; margin: 0 14px; flex-shrink: 0;
        background: linear-gradient(90deg, transparent, rgba(201,151,58,0.3), transparent);
      }

      .sidebar-nav {
        flex: 1; padding: 12px 8px 8px;
        display: flex; flex-direction: column; gap: 2px;
        overflow-y: auto; overflow-x: hidden;
      }
      .sidebar-nav::-webkit-scrollbar { width: 2px; }
      .sidebar-nav::-webkit-scrollbar-thumb { background: rgba(201,151,58,0.18); border-radius: 2px; }

      .nav-item {
        display: flex; align-items: center; gap: 11px;
        padding: 10px 12px; border-radius: 2px;
        color: rgba(245,228,176,0.75);
        font-family: 'Cinzel', serif; font-size: 12px; font-weight: 600; letter-spacing: 0.8px;
        white-space: nowrap; transition: all 0.22s ease;
        position: relative; overflow: visible;
        background: none; border: none; cursor: pointer; width: 100%; text-align: left;
      }
      .nav-item::before {
        content: ''; position: absolute; inset: 0;
        background: linear-gradient(90deg, rgba(201,151,58,0.1) 0%, rgba(201,151,58,0.04) 60%, transparent 100%);
        opacity: 0; transition: opacity 0.22s; border-radius: 2px;
      }
      .nav-item:hover { color: rgba(245,228,176,1); }
      .nav-item:hover::before { opacity: 1; }
      .nav-item.active { color: var(--gold-bright); }
      .nav-item.active::before {
        opacity: 1;
        background: linear-gradient(90deg, rgba(201,151,58,0.22) 0%, rgba(201,151,58,0.08) 70%, transparent 100%);
      }
      .nav-item.active::after {
        content: ''; position: absolute; left: 0; top: 10%; bottom: 10%; width: 3px;
        background: linear-gradient(180deg, transparent, var(--gold-bright), var(--gold-mid), transparent);
        border-radius: 0 2px 2px 0; box-shadow: 0 0 8px rgba(232,196,106,0.5);
      }
      .nav-icon { font-size: 15px; flex-shrink: 0; width: 20px; text-align: center; filter: drop-shadow(0 0 3px rgba(232,196,106,0.2)); }
      .nav-item.active .nav-icon { filter: drop-shadow(0 0 6px rgba(232,196,106,0.5)); }
      .nav-label { flex: 1; }
      .nav-arrow { font-size: 14px; opacity: 0.3; font-family: serif; transition: opacity 0.2s, transform 0.2s; }
      .nav-item.active .nav-arrow { opacity: 0.7; transform: translateX(2px); color: var(--gold-mid); }
      .nav-tooltip {
        position: absolute; left: calc(100% + 12px); top: 50%; transform: translateY(-50%);
        background: linear-gradient(135deg, #2c0a0a, #1a0404);
        border: 1px solid rgba(201,151,58,0.3); color: var(--gold-bright);
        font-family: 'Cinzel', serif; font-size: 11px; font-weight: 600; letter-spacing: 1px;
        padding: 6px 12px; white-space: nowrap; pointer-events: none; opacity: 0;
        transition: opacity 0.18s; z-index: 999;
        clip-path: polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%);
        box-shadow: 4px 4px 12px rgba(26,4,4,0.4);
      }
      .nav-item:hover .nav-tooltip { opacity: 1; }

      .sidebar-footer { padding: 10px 0 0; flex-shrink: 0; }
      .sidebar-footer-inner {
        display: flex; flex-direction: column; align-items: center;
        gap: 3px; padding: 10px 14px 8px; overflow: hidden;
      }
      .sf-ornament { font-family: 'Cinzel', serif; font-size: 10px; letter-spacing: 3px; color: rgba(232,196,106,0.3); }
      .sf-temple {
        font-family: 'Cinzel', serif; font-size: 10.5px; font-weight: 700; letter-spacing: 1px;
        background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid));
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        text-align: center; white-space: normal; overflow: visible; max-width: 90%;
        line-height: 1.4; text-shadow: 0 0 10px rgba(232,196,106,0.2);
        animation: subtlePulse 4s ease-in-out infinite;
      }
      @keyframes subtlePulse {
        0%, 100% { filter: brightness(1) drop-shadow(0 0 2px rgba(232,196,106,0.1)); }
        50% { filter: brightness(1.2) drop-shadow(0 0 6px rgba(232,196,106,0.3)); }
      }
      .sf-copy { font-family: 'EB Garamond', serif; font-style: italic; font-size: 10.5px; color: rgba(232,196,106,0.35); }
      .sf-collapsed-om { font-family: 'Cinzel Decorative', cursive; font-size: 14px; color: rgba(232,196,106,0.2); text-align: center; padding: 10px 0 8px; }
      .sidebar-bottom-strip {
        height: 3px;
        background: linear-gradient(90deg, transparent, rgba(201,151,58,0.2) 40%, rgba(201,151,58,0.4) 50%, rgba(201,151,58,0.2) 60%, transparent);
        margin-top: 4px;
      }

      /* ══ MAIN COLUMN — flex column, fills remaining width ══ */
      .admin-main {
        flex: 1;
        min-width: 0;           /* prevent flex blowout */
        height: 100vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;       /* children handle their own scroll */
        transition: none;       /* sidebar width change handles offset */
      }

      /* ══ TOPBAR — fixed height, never shrinks ══ */
      .admin-topbar {
        height: var(--topbar-h);
        flex-shrink: 0;         /* KEY: topbar always visible */
        background:
          radial-gradient(ellipse at 0% 50%, rgba(160,35,35,0.25) 0%, transparent 50%),
          linear-gradient(135deg, #2a0909 0%, #1c0505 50%, #130303 100%);
        border-bottom: 1px solid rgba(201,151,58,0.18);
        display: flex; align-items: center; justify-content: space-between;
        padding: 0 24px;
        position: relative;
        z-index: 50;
        box-shadow: 0 4px 20px rgba(26,4,4,0.35);
      }
      .topbar-gold-line {
        position: absolute; bottom: 0; left: 0; right: 0; height: 1px;
        background: linear-gradient(90deg, transparent, var(--gold-dim) 20%, var(--gold-bright) 50%, var(--gold-dim) 80%, transparent);
        pointer-events: none;
      }
      .topbar-left { display: flex; align-items: center; gap: 16px; }
      .topbar-right { display: flex; align-items: center; gap: 10px; }

      .toggle-btn {
        background: rgba(255,255,255,0.04); border: 1px solid rgba(201,151,58,0.18);
        cursor: pointer; padding: 8px 10px; border-radius: 2px;
        transition: background 0.2s, border-color 0.2s;
      }
      .toggle-btn:hover { background: rgba(201,151,58,0.1); border-color: rgba(201,151,58,0.35); }
      .toggle-icon { display: flex; flex-direction: column; gap: 4px; width: 18px; }
      .toggle-icon span {
        display: block; height: 1.5px; background: var(--gold-bright);
        border-radius: 1px; transition: all 0.3s ease; opacity: 0.8;
      }
      .toggle-icon.open span:nth-child(1) { transform: translateY(5.5px) rotate(45deg); }
      .toggle-icon.open span:nth-child(2) { opacity: 0; transform: scaleX(0); }
      .toggle-icon.open span:nth-child(3) { transform: translateY(-5.5px) rotate(-45deg); }

      .breadcrumb {
        font-family: 'Cinzel', serif; font-size: 10px; letter-spacing: 1.2px;
        display: flex; align-items: center; gap: 5px;
      }
      .bc-root { color: rgba(232,196,106,0.35); }
      .bc-sep  { color: rgba(232,196,106,0.3); font-size: 12px; }
      .bc-current { color: var(--gold-bright); font-weight: 700; letter-spacing: 1.5px; }

      .topbar-btn {
        display: flex; align-items: center; gap: 6px;
        background: rgba(255,255,255,0.04);
        border: 1px solid rgba(201,151,58,0.22);
        padding: 7px 13px; cursor: pointer;
        font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 600; letter-spacing: 1px;
        color: rgba(245,228,176,0.7);
        clip-path: polygon(8px 0%, calc(100% - 8px) 0%, 100% 50%, calc(100% - 8px) 100%, 8px 100%, 0% 50%);
        transition: all 0.2s;
      }
      .topbar-btn:hover { background: rgba(201,151,58,0.12); border-color: rgba(201,151,58,0.45); color: var(--gold-bright); }
      .tb-label { display: none; }
      @media (min-width: 600px) { .tb-label { display: inline; } }
      .tb-caret { font-size: 9px; opacity: 0.5; }

      .lang-wrapper { position: relative; }
      .lang-dropdown {
        position: absolute; top: calc(100% + 10px); right: 0;
        background: linear-gradient(145deg, #2c0a0a, #1a0404);
        border: 1px solid rgba(201,151,58,0.28); min-width: 155px;
        box-shadow: 0 12px 32px rgba(26,4,4,0.5);
        clip-path: polygon(0 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%);
        z-index: 200; animation: dropIn 0.2s ease;
      }
      @keyframes dropIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
      .lang-opt {
        display: flex; align-items: center; gap: 10px;
        width: 100%; padding: 11px 16px;
        background: none; border: none; cursor: pointer;
        font-family: 'Cinzel', serif; font-size: 10px; font-weight: 600; letter-spacing: 1px;
        color: rgba(245,228,176,0.6); transition: background 0.2s, color 0.2s; text-align: left;
      }
      .lang-opt:hover { background: rgba(201,151,58,0.1); color: var(--gold-bright); }
      .lang-opt.active { color: var(--gold-bright); background: rgba(201,151,58,0.08); }
      .lang-opt + .lang-opt { border-top: 1px solid rgba(201,151,58,0.1); }

      .admin-badge {
        display: flex; align-items: center; gap: 10px;
        padding: 5px 14px 5px 5px;
        border: 1px solid rgba(201,151,58,0.22);
        background: rgba(201,151,58,0.06);
        clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%);
      }
      .badge-avatar {
        width: 30px; height: 30px; border-radius: 50%;
        background: radial-gradient(circle, #5a1515, #2a0707);
        border: 1px solid rgba(232,196,106,0.4);
        display: flex; align-items: center; justify-content: center;
        position: relative; font-family: 'Cinzel Decorative', cursive;
        font-size: 13px; font-weight: 700; color: var(--gold-bright);
        flex-shrink: 0; box-shadow: 0 0 10px rgba(232,196,106,0.15);
      }
      .badge-ring {
        position: absolute; inset: -2px; border-radius: 50%;
        border: 1px dashed rgba(201,151,58,0.3);
        animation: slowSpin 8s linear infinite;
      }
      .badge-info { display: none; flex-direction: column; }
      @media (min-width: 680px) { .badge-info { display: flex; } }
      .badge-name { font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1px; color: rgba(245,228,176,0.9); }
      .badge-role { font-family: 'EB Garamond', serif; font-style: italic; font-size: 11px; color: rgba(232,196,106,0.4); }
      .logout-btn { border-color: rgba(180,40,40,0.3); color: rgba(220,100,100,0.8); }
      .logout-btn:hover { background: rgba(180,40,40,0.12); border-color: rgba(180,40,40,0.5); color: #e88080; }

      /* ══ CONTENT WRAPPER — fills remaining height after topbar ══ */
      .admin-content-wrap {
        flex: 1;                 /* take all remaining vertical space */
        min-height: 0;           /* KEY: allow flex child to shrink below content size */
        overflow: hidden;        /* clip, let .admin-content scroll */
        background: radial-gradient(ellipse at 80% 0%, rgba(201,151,58,0.04) 0%, transparent 50%), var(--parchment);
        position: relative;
      }

      /* ══ ADMIN CONTENT — the only scrollable region ══ */
      .admin-content {
        height: 100%;
        overflow-y: auto;        /* ONLY this scrolls */
        overflow-x: hidden;
        padding: 28px 28px 40px;
        animation: pageIn 0.35s ease both;
        /* Custom scrollbar */
        scrollbar-width: thin;
        scrollbar-color: rgba(201,151,58,0.35) rgba(201,151,58,0.06);
      }
      .admin-content::-webkit-scrollbar { width: 6px; }
      .admin-content::-webkit-scrollbar-track { background: rgba(201,151,58,0.05); }
      .admin-content::-webkit-scrollbar-thumb {
        background: linear-gradient(180deg, var(--gold-dim), var(--gold-mid));
        border-radius: 3px;
      }
      .admin-content::-webkit-scrollbar-thumb:hover { background: var(--gold-mid); }

      @keyframes pageIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }

      /* ══ DASHBOARD ══ */
      .dash-page { display: flex; flex-direction: column; gap: 24px; }
      .dash-welcome {
        display: flex; align-items: center; justify-content: space-between;
        padding: 20px 28px;
        background: linear-gradient(135deg, #2c0a0a 0%, #1a0404 60%, #0e0101 100%);
        border: 1px solid rgba(201,151,58,0.2);
        clip-path: polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px));
        box-shadow: 0 8px 24px rgba(26,4,4,0.2);
      }
      .dash-greeting { font-family: 'EB Garamond', serif; font-style: italic; font-size: 14px; color: rgba(245,228,176,0.5); }
      .dash-heading { font-family: 'Cinzel Decorative', cursive; font-size: 20px; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .dash-welcome-ornament { font-family: 'Cinzel Decorative', cursive; font-size: 42px; background: linear-gradient(180deg, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; opacity: 0.25; }

      .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
      @media (max-width: 1100px) { .stats-grid { grid-template-columns: repeat(2, 1fr); } }
      @media (max-width: 580px)  { .stats-grid { grid-template-columns: 1fr; } }

      .stat-card {
        background: linear-gradient(145deg, #FDF3E3 0%, #f5e8cc 100%);
        border: 1px solid rgba(201,151,58,0.25); padding: 18px 20px;
        display: flex; align-items: flex-start; gap: 14px;
        clip-path: polygon(0 0, calc(100% - 14px) 0, 100% 14px, 100% 100%, 14px 100%, 0 calc(100% - 14px));
        position: relative; overflow: hidden;
        box-shadow: 0 4px 16px rgba(26,4,4,0.07);
        animation: cardIn 0.5s ease both;
        transition: transform 0.2s, box-shadow 0.2s;
      }
      .stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(26,4,4,0.12); }
      @keyframes cardIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      .stat-icon-box { flex-shrink: 0; display: flex; align-items: center; }
      .stat-body { flex: 1; }
      .stat-label { font-family: 'Cinzel', serif; font-size: 8.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-dim); margin-bottom: 4px; }
      .stat-value { font-family: 'Cinzel Decorative', cursive; font-size: 20px; background: linear-gradient(135deg, var(--crimson-rich), var(--crimson-deep)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.2; }
      .stat-delta { font-family: 'EB Garamond', serif; font-style: italic; font-size: 12px; margin-top: 3px; }
      .stat-delta.pos { color: #2a6b2a; }
      .stat-delta.neg { color: #8b1a1a; }
      .stat-glow { position: absolute; bottom: -20px; right: -20px; width: 80px; height: 80px; border-radius: 50%; background: radial-gradient(circle, rgba(201,151,58,0.08) 0%, transparent 70%); pointer-events: none; }

      .dash-bottom { display: grid; grid-template-columns: 1.3fr 1fr; gap: 20px; }
      @media (max-width: 880px) { .dash-bottom { grid-template-columns: 1fr; } }

      .activity-card, .quick-card {
        background: linear-gradient(145deg, #FDF3E3 0%, #f5e8cc 100%);
        border: 1px solid rgba(201,151,58,0.22); padding: 0;
        clip-path: polygon(0 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%);
        box-shadow: 0 4px 16px rgba(26,4,4,0.07); overflow: hidden;
      }
      .card-header {
        display: flex; align-items: center; gap: 10px; padding: 14px 20px;
        border-bottom: 1px solid rgba(201,151,58,0.15); background: rgba(201,151,58,0.04);
      }
      .card-title { font-family: 'Cinzel', serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); }

      .activity-list { padding: 8px 0; }
      .activity-item { display: flex; align-items: flex-start; gap: 12px; padding: 10px 20px; transition: background 0.15s; }
      .activity-item:hover { background: rgba(201,151,58,0.04); }
      .activity-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; margin-top: 5px; }
      .activity-dot.success { background: #2a6b2a; box-shadow: 0 0 6px rgba(42,107,42,0.4); }
      .activity-dot.info    { background: var(--gold-mid); box-shadow: 0 0 6px rgba(201,151,58,0.4); }
      .activity-dot.neutral { background: rgba(74,26,8,0.25); }
      .activity-text { display: flex; flex-direction: column; gap: 2px; }
      .activity-msg { font-family: 'EB Garamond', serif; font-size: 14px; color: var(--text-mid); }
      .activity-time { font-family: 'Cinzel', serif; font-size: 8.5px; letter-spacing: 1px; color: rgba(74,26,8,0.35); }

      .quick-bars { padding: 16px 20px; display: flex; flex-direction: column; gap: 14px; }
      .quick-bar-item { display: flex; align-items: center; gap: 10px; }
      .quick-bar-label { font-family: 'Cinzel', serif; font-size: 9px; font-weight: 600; letter-spacing: 1px; color: var(--text-mid); width: 80px; flex-shrink: 0; }
      .quick-bar-track { flex: 1; height: 5px; background: rgba(74,26,8,0.08); border-radius: 2px; overflow: hidden; }
      .quick-bar-fill { height: 100%; border-radius: 2px; transition: width 1s ease; }
      .quick-bar-pct { font-family: 'Cinzel', serif; font-size: 9px; color: var(--gold-dim); width: 32px; text-align: right; flex-shrink: 0; }

      /* ══ STUB PAGE ══ */
      .stub-page {
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        min-height: 60vh; gap: 16px; text-align: center; animation: pageIn 0.35s ease both;
      }
      .stub-title { font-family: 'Cinzel Decorative', cursive; font-size: 22px; background: linear-gradient(135deg, var(--crimson-rich), var(--crimson-deep)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .stub-desc { font-family: 'EB Garamond', serif; font-style: italic; font-size: 16px; color: rgba(74,26,8,0.45); }
      .stub-ornament { font-family: 'Cinzel', serif; font-size: 14px; color: rgba(201,151,58,0.4); letter-spacing: 4px; margin-top: 8px; }

      /* ══ GOLD ICON ══ */
      .gold-icon-wrap {
        display: inline-flex; align-items: center; justify-content: center;
        background: linear-gradient(135deg, #fff8e7, var(--gold-bright), var(--gold-mid));
        -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; line-height: 1;
      }
      .gold-icon-glow { filter: drop-shadow(0 0 6px rgba(232,196,106,0.45)); }

      /* ══ MEDALLION FACES ══ */
      .sm-face {
        position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
        border-radius: 50%; opacity: 0; transform: scale(0.75) rotateY(90deg);
        transition: opacity 0.55s ease, transform 0.55s ease;
      }
      .sm-face-visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .sm-logo-img { width: 26px; height: 26px; object-fit: contain; filter: drop-shadow(0 0 4px rgba(232,196,106,0.4)); }

      /* ══ MOBILE OVERLAY ══ */
      .mobile-overlay {
        position: fixed; inset: 0; z-index: 99;
        background: rgba(10,2,2,0.65); backdrop-filter: blur(2px);
        animation: overlayIn 0.2s ease;
      }
      @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }

      /* ══ MOBILE CONTROLS ══ */
      .mobile-sidebar-close { display: none; }
      .mobile-toggle { display: none; }
      .desktop-toggle { display: flex; }
      .mobile-bottom-nav { display: none; }

      /* ══ RESPONSIVE ══ */
      @media (max-width: 1024px) and (min-width: 769px) {
        .admin-content { padding: 20px 20px 32px; }
        .stats-grid { grid-template-columns: repeat(2, 1fr); }
      }

      @media (max-width: 768px) {
        /* On mobile, sidebar slides over the content */
        .admin-sidebar {
          width: 260px !important;
          position: fixed;
          top: 0; left: 0;
          transform: translateX(-100%);
          transition: transform var(--transition);
          z-index: 200;
        }
        .admin-sidebar.mobile-sidebar-open { transform: translateX(0); }

        .mobile-sidebar-close {
          display: flex; align-items: center; justify-content: center;
          margin-left: auto;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(201,151,58,0.2); border-radius: 50%;
          width: 28px; height: 28px; cursor: pointer;
          color: rgba(232,196,106,0.6); flex-shrink: 0; transition: background 0.2s, color 0.2s;
        }
        .mobile-sidebar-close:hover { background: rgba(201,151,58,0.12); color: var(--gold-bright); }

        .sidebar-brand  { display: block !important; }
        .sidebar-temple-sub { display: flex !important; }
        .nav-label  { display: inline !important; }
        .nav-arrow  { display: inline !important; }

        /* On mobile the main fills the full width */
        .admin-main { width: 100vw; }

        .desktop-toggle { display: none; }
        .mobile-toggle  { display: flex; }

        .admin-topbar { padding: 0 14px; }
        .tb-label { display: none !important; }
        .badge-info { display: none !important; }

        .admin-content { padding: 16px 14px 90px; }

        .dash-welcome { padding: 16px 18px; }
        .dash-heading { font-size: 16px; }
        .dash-welcome-ornament { font-size: 28px; }

        .stats-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
        .stat-card { padding: 14px 14px; gap: 10px; }
        .stat-value { font-size: 16px; }

        .dash-bottom { grid-template-columns: 1fr; gap: 14px; }
        .stub-title { font-size: 18px; }

        /* ── MOBILE BOTTOM NAV ── */
        .mobile-bottom-nav {
          display: flex;
          height: 62px; flex-shrink: 0;
          background: linear-gradient(135deg, #2a0909 0%, #1c0505 50%, #130303 100%);
          border-top: 1px solid rgba(201,151,58,0.2);
          box-shadow: 0 -4px 20px rgba(26,4,4,0.4);
          justify-content: space-around; align-items: stretch; padding: 0 4px;
          position: relative;
        }
        .mobile-bottom-nav::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 1px;
          background: linear-gradient(90deg, transparent, var(--gold-mid), transparent);
        }
        .mbn-item {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 3px; flex: 1; color: rgba(245,228,176,0.4);
          background: none; border: none; cursor: pointer; transition: color 0.2s;
          padding: 6px 2px; position: relative;
        }
        .mbn-item:hover { color: rgba(245,228,176,0.75); }
        .mbn-item.active { color: var(--gold-bright); }
        .mbn-item.active::before {
          content: ''; position: absolute; top: 0; left: 20%; right: 20%; height: 2px;
          background: linear-gradient(90deg, transparent, var(--gold-bright), transparent);
          border-radius: 0 0 2px 2px;
        }
        .mbn-label {
          font-family: 'Cinzel', serif; font-size: 7px; font-weight: 600; letter-spacing: 0.5px;
          text-transform: uppercase; white-space: nowrap; overflow: hidden;
          max-width: 52px; text-overflow: ellipsis;
        }
      }

      @media (max-width: 420px) {
        .stats-grid { grid-template-columns: 1fr; }
        .breadcrumb { font-size: 9px; }
        .mbn-label { display: none; }
        .mobile-bottom-nav { height: 54px; }
        .admin-content { padding-bottom: 70px; }
      }

      /* ══ LOGOUT CONFIRM MODAL ══ */
      .logout-overlay {
        position: fixed; inset: 0; z-index: 9999;
        background: rgba(10,2,2,0.72); backdrop-filter: blur(4px);
        display: flex; align-items: center; justify-content: center;
        padding: 20px;
        animation: overlayIn 0.2s ease;
      }
      .logout-modal {
        position: relative; width: 100%; max-width: 380px;
        background: linear-gradient(160deg, #FDF3E3, #faf0dc 55%, #f5e8cc);
        border: 1px solid rgba(201,151,58,0.38);
        clip-path: polygon(0 18px,18px 0,calc(100% - 18px) 0,100% 18px,100% calc(100% - 18px),calc(100% - 18px) 100%,18px 100%,0 calc(100% - 18px));
        box-shadow: 0 32px 90px rgba(26,4,4,0.7);
        animation: regalIn 0.3s cubic-bezier(0.23,1,0.32,1) both;
        overflow: hidden; text-align: center; padding-bottom: 6px;
      }
      @keyframes regalIn { from { opacity:0; transform: scale(0.92) translateY(16px); } to { opacity:1; transform: scale(1) translateY(0); } }
      .lm-topstrip {
        height: 3px;
        background: linear-gradient(90deg, transparent, #8b1a1a88, #8b1a1a, #8b1a1a88, transparent);
        margin-bottom: 28px;
      }
      .lm-corner {
        position: absolute; width: 10px; height: 10px;
        border-color: rgba(201,151,58,0.45); border-style: solid;
      }
      .lm-corner.tl { top: 12px; left: 12px; border-width: 1px 0 0 1px; }
      .lm-corner.tr { top: 12px; right: 12px; border-width: 1px 1px 0 0; }
      .lm-corner.bl { bottom: 8px; left: 12px; border-width: 0 0 1px 1px; }
      .lm-corner.br { bottom: 8px; right: 12px; border-width: 0 1px 1px 0; }
      .lm-icon-wrap {
        position: relative; width: 68px; height: 68px; border-radius: 50%;
        background: rgba(139,26,26,0.12); border: 1px solid rgba(139,26,26,0.3);
        display: flex; align-items: center; justify-content: center;
        margin: 0 auto 12px;
      }
      .lm-icon-ring {
        position: absolute; inset: -5px; border-radius: 50%;
        border: 1px dashed rgba(139,26,26,0.3);
        animation: slowSpin 8s linear infinite;
      }
      .lm-icon-ring2 { inset: -12px; animation-duration: 14s; animation-direction: reverse; }
      @keyframes slowSpin { to { transform: rotate(360deg); } }
      .lm-icon { font-size: 26px; color: #8b1a1a; position: relative; z-index: 1; display: flex; }
      .lm-eyebrow {
        font-family: 'Cinzel', serif; font-size: 7.5px; letter-spacing: 3px;
        color: rgba(74,26,8,0.32); text-transform: uppercase; margin-bottom: 6px;
      }
      .lm-title {
        font-family: 'Cinzel Decorative', cursive; font-size: 20px; font-weight: 900;
        background: linear-gradient(135deg, #8b1a1a, #c9973a);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        margin-bottom: 2px;
      }
      .lm-divider {
        height: 1px; margin: 14px 36px;
        background: linear-gradient(90deg, transparent, rgba(201,151,58,0.35), transparent);
        display: flex; align-items: center; justify-content: center; position: relative;
      }
      .lm-gem {
        width: 6px; height: 6px; transform: rotate(45deg);
        background: rgba(201,151,58,0.55); position: absolute;
      }
      .lm-message {
        font-family: 'EB Garamond', serif; font-size: 16px;
        color: rgba(74,26,8,0.75); line-height: 1.7;
        padding: 0 32px 20px;
      }
      .lm-actions {
        display: flex; gap: 10px; justify-content: center;
        flex-wrap: wrap; padding: 0 28px 20px;
      }
      .lm-btn {
        display: inline-flex; align-items: center; gap: 7px;
        font-family: 'Cinzel', serif; font-size: 9.5px; font-weight: 700;
        letter-spacing: 1.5px; text-transform: uppercase;
        padding: 11px 22px; cursor: pointer;
        clip-path: polygon(10px 0%,calc(100% - 10px) 0%,100% 50%,calc(100% - 10px) 100%,10px 100%,0 50%);
        transition: all 0.2s; border: 1px solid transparent;
      }
      .lm-cancel {
        background: none; color: rgba(74,26,8,0.8);
        border-color: rgba(149,27,27,0.4);
      }
      .lm-cancel:hover { background: rgba(201,151,58,0.08); border-color: rgba(149,27,27,0.6); }
      .lm-confirm {
        background: linear-gradient(135deg,#5a0000,#8b1a1a);
        color: #ffe8e8; border-color: rgba(139,26,26,0.4);
        box-shadow: 0 4px 16px rgba(139,26,26,0.3);
      }
      .lm-confirm:hover { filter: brightness(1.12); box-shadow: 0 6px 22px rgba(139,26,26,0.45); }
      .lm-bottomstrip {
        height: 1px; margin: 0 24px 14px;
        background: linear-gradient(90deg, transparent, rgba(201,151,58,0.22), transparent);
      }
      @media (max-width: 768px) {
        .logout-modal {
          clip-path: none !important;
          border-radius: 24px 24px 0 0 !important;
          max-width: 100%; position: fixed;
          bottom: 0; left: 0; right: 0;
          border-bottom: none;
          animation: slideUpRegal 0.4s cubic-bezier(0.25, 1, 0.5, 1) both;
        }
        .logout-overlay { align-items: flex-end; padding: 0; }
        @keyframes slideUpRegal { from { transform: translateY(100%); } to { transform: translateY(0); } }
        
        .lm-topstrip { height: 4px; margin-bottom: 24px; }
        .lm-title { font-size: 21px; }
        .lm-message { font-size: 15px; padding: 0 24px 24px; color: rgba(74,26,8,0.85); }
        .lm-actions { padding: 0 24px 28px; gap: 10px; flex-direction: column; }
        .lm-btn { clip-path: none; border-radius: 30px; width: 100%; justify-content: center; padding: 14px; font-size: 10px; }
        .lm-corner { display: none; }
        .lm-icon-wrap { width: 62px; height: 62px; margin-bottom: 10px; }
        .lm-icon { font-size: 24px; }
      }
    `}</style>
  );
}